/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react'
import { css } from '@emotion/react'
import { Grid, LinearProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useGetDashboardStatsQuery } from '../orders/orders.service'
import { DashboardRangePicker } from './dashboard-range-picker'
import { DashboardSummaryCards } from './dashboard-summary-cards'
import { SalesOverTimeChart } from './sales-over-time-chart'
import { ProductMonthComparisonChart } from './product-month-comparison-chart'
import { ProductTypeDonut } from './product-type-donut'
import { TopListChart } from './top-list-chart'
import { CHART_COLORS, DEFAULT_RANGE, getRange, RangeKey } from './dashboard-helpers'

const styles = {
    header: css`
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;
    `,
    progress: css`
        height: 2px;
        margin-bottom: 8px;
    `,
}

export const DashboardView: React.FC = () => {
    const { t } = useTranslation()
    const [range, setRange] = useState<RangeKey>(DEFAULT_RANGE)
    const query = useMemo(() => getRange(range), [range])
    const { data, isFetching } = useGetDashboardStatsQuery(query)

    const topProducts = (data?.topProducts ?? []).map((p) => ({ name: p.name, count: p.count, revenue: p.revenue }))
    const topRealtors = (data?.topRealtors ?? []).map((r) => ({ name: r.name, count: r.count, revenue: r.revenue }))
    const topCompanies = (data?.topCompanies ?? []).map((c) => ({ name: c.name, count: c.count, revenue: c.revenue }))

    return (
        <div>
            <div css={styles.header}>
                <DashboardRangePicker value={range} onChange={setRange} />
            </div>
            <div css={styles.progress}>{isFetching && <LinearProgress />}</div>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <DashboardSummaryCards summary={data?.summary} />
                </Grid>

                <Grid item xs={12} md={8}>
                    <SalesOverTimeChart data={data?.monthly ?? []} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ProductTypeDonut data={data?.byType ?? []} />
                </Grid>

                <Grid item xs={12}>
                    <ProductMonthComparisonChart data={data?.productMonthly ?? []} />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TopListChart title={t('TEXT_TOP_PRODUCTS', 'Topp 5 produkter')} data={topProducts} color={CHART_COLORS[0]} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TopListChart title={t('TEXT_TOP_REALTORS', 'Topp 5 meglere')} data={topRealtors} color={CHART_COLORS[2]} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TopListChart title={t('TEXT_TOP_COMPANIES', 'Topp 5 meglerkontorer')} data={topCompanies} color={CHART_COLORS[4]} />
                </Grid>
            </Grid>
        </div>
    )
}
